import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
  Tab,
  Tabs,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';
import RichTextEditor from '../../components/RichTextEditor';

// Available writing styles (from blog_styles.py)
const PRIMARY_STYLES = {
  contrarian: { name: 'Contrarian & Provocative', description: 'Challenge the dominant narrative with thoughtful disagreement' },
  future_focused: { name: 'Future-Focused', description: 'Identify emerging patterns and predict what comes next' },
  deconstructive: { name: 'Deconstructive', description: 'Take apart and analyze why things work or fail' },
  connective: { name: 'Connective', description: 'Draw unexpected parallels between unrelated domains' },
  provocative_inquiry: { name: 'Provocative Inquiry', description: 'Ask the questions no one else is asking' },
  meta_commentary: { name: 'Meta & Industry Commentary', description: 'Insider perspective on industry patterns and unspoken truths' },
  manifesto: { name: 'Manifesto Style', description: 'Take a declarative stance and draw a line in the sand' },
};

const SECONDARY_STYLES = {
  experiential: { name: 'Experiential', description: 'Evidence-based insights from observable patterns' },
  experimental_format: { name: 'Experimental Formats', description: 'Creative structures that break conventional format' },
  statistical: { name: 'Statistical Insights', description: 'Data analysis using public research and industry data' },
};

const STRUCTURAL_ELEMENTS = {
  contrarian_setup: { name: 'Contrarian Setup', description: 'State conventional wisdom, then explain why it\'s wrong' },
  proof_point: { name: 'Proof Point', description: 'One verifiable event or real company action' },
  stakes_frame: { name: 'Stakes Frame', description: 'What happens if the reader does nothing' },
};
const DEFAULT_ELEMENTS = ['proof_point', 'stakes_frame'];

/* Star rating display — accepts float score (1.0-5.0), renders filled/partial/empty stars */
function StarRating({ score, size = 16 }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const fill = Math.min(1, Math.max(0, score - (i - 1)));
    if (fill >= 1) {
      stars.push(
        <span key={i} style={{ color: colors.goldMid, fontSize: size }}>★</span>
      );
    } else if (fill > 0) {
      // Partial star via clipped overlay
      stars.push(
        <span key={i} style={{ position: 'relative', display: 'inline-block', fontSize: size }}>
          <span style={{ color: colors.greyLight }}>★</span>
          <span style={{
            position: 'absolute', left: 0, top: 0,
            overflow: 'hidden', width: `${fill * 100}%`,
            color: colors.goldMid,
          }}>★</span>
        </span>
      );
    } else {
      stars.push(
        <span key={i} style={{ color: colors.greyLight, fontSize: size }}>★</span>
      );
    }
  }
  return <span style={{ whiteSpace: 'nowrap' }}>{stars}</span>;
}

function LongFormPosts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [approvedPosts, setApprovedPosts] = useState([]);
  const [readiness, setReadiness] = useState(null);

  // Modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [modalError, setModalError] = useState('');

  // Generation states
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedThesis, setSelectedThesis] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [customThesis, setCustomThesis] = useState('');
  const [customOutcome, setCustomOutcome] = useState('');

  // Edit states
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editThesis, setEditThesis] = useState('');
  const [editOutcome, setEditOutcome] = useState('');

  // Regenerate state
  const [regeneratingDraft, setRegeneratingDraft] = useState(false);
  const [previewThesis, setPreviewThesis] = useState('');
  const [previewOutcome, setPreviewOutcome] = useState('');
  const [previewPrimaryStyle, setPreviewPrimaryStyle] = useState('');
  const [previewSecondaryStyle, setPreviewSecondaryStyle] = useState('');

  // Featured image state
  const [generatingImage, setGeneratingImage] = useState(false);
  const [featuredImageUrl, setFeaturedImageUrl] = useState(null);

  // Category state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Topic mode selector
  const [topicMode, setTopicMode] = useState('emerging');
  const [hasThoughtLeaders, setHasThoughtLeaders] = useState(false);

  // Custom topic entry mode
  const [customTopicMode, setCustomTopicMode] = useState(false);
  const [customTopicInput, setCustomTopicInput] = useState('');
  const [customTopicDescription, setCustomTopicDescription] = useState('');
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);

  // Style selection
  const [selectedPrimaryStyle, setSelectedPrimaryStyle] = useState('');
  const [selectedSecondaryStyle, setSelectedSecondaryStyle] = useState('');

  // Structural elements
  const [selectedElements, setSelectedElements] = useState(DEFAULT_ELEMENTS);
  const [previewElements, setPreviewElements] = useState(DEFAULT_ELEMENTS);

  // Business context for topic variety
  const [businessContext, setBusinessContext] = useState({
    core_product: '',
    features_benefits: '',
  });
  const [editingBusinessContext, setEditingBusinessContext] = useState(false);
  const [savingBusinessContext, setSavingBusinessContext] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, draftsRes, readinessRes, businessRes, leadersRes] = await Promise.all([
        fetch('/api/content/profile', { headers: getAuthHeaders() }),
        fetch('/api/content/drafts?content_type=long_form', { headers: getAuthHeaders() }),
        fetch('/api/content/readiness', { headers: getAuthHeaders() }),
        fetch('/api/business/fields/by-names', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ field_names: ['core_product', 'features_benefits'] }),
        }),
        fetch('/api/industry-pulse/thought-leaders', { credentials: 'include' }),
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.profile);
      }

      if (businessRes.ok) {
        const data = await businessRes.json();
        const fields = data.fields || {};
        setBusinessContext({
          core_product: fields.core_product || '',
          features_benefits: fields.features_benefits || '',
        });
      }

      if (leadersRes.ok) {
        const data = await leadersRes.json();
        setHasThoughtLeaders((data.thought_leaders || []).length > 0);
      }

      if (draftsRes.ok) {
        const data = await draftsRes.json();
        const allDrafts = data.drafts || [];
        setDrafts(allDrafts.filter((d) => d.status === 'draft'));
        setApprovedPosts(allDrafts.filter((d) => d.status === 'approved'));
      }

      if (readinessRes.ok) {
        const data = await readinessRes.json();
        setReadiness(data);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await fetch('/api/publish/categories', { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const saveBusinessContext = async () => {
    try {
      setSavingBusinessContext(true);
      const res = await fetch('/api/business/fields', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fields: businessContext }),
      });
      if (res.ok) {
        setEditingBusinessContext(false);
      } else {
        setError('Failed to save business context');
      }
    } catch (err) {
      setError('Failed to save business context');
    } finally {
      setSavingBusinessContext(false);
    }
  };

  const generateTopics = async (retryCount = 0) => {
    const maxRetries = 3;
    try {
      setGeneratingTopics(true);
      if (retryCount > 0) {
        setError(`Hang tight, experiencing a technical issue... (attempt ${retryCount + 1}/${maxRetries})`);
      }
      const res = await fetch('/api/content/generate-topics', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ count: 5, topic_mode: topicMode }),
      });

      if (res.ok) {
        const data = await res.json();
        const topics = data.topics || [];

        // If no topics and we haven't exhausted retries, try again
        if (topics.length === 0 && retryCount < maxRetries - 1) {
          setGeneratingTopics(false);
          return generateTopics(retryCount + 1);
        }

        // If still no topics after all retries, show error and alert admin
        if (topics.length === 0) {
          setError('Something has gone wrong generating topics. The admin has been notified.');
          // Send admin alert
          fetch('/api/admin/alert', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              type: 'topic_generation_failed',
              message: 'Topic generation failed after 3 attempts',
            }),
          }).catch(() => {}); // Fire and forget
          return;
        }

        setError('');
        setSuggestedTopics(topics);
      } else {
        setError('Failed to generate topics');
      }
    } catch (err) {
      setError('Failed to generate topics');
    } finally {
      setGeneratingTopics(false);
    }
  };

  const selectTopic = (topic) => {
    setSelectedTopic(topic);
    // Pre-select first suggestions
    if (topic.suggested_theses && topic.suggested_theses.length > 0) {
      setSelectedThesis(topic.suggested_theses[0]);
    }
    if (topic.suggested_outcomes && topic.suggested_outcomes.length > 0) {
      setSelectedOutcome(topic.suggested_outcomes[0]);
    }
    // Set style from topic (or defaults)
    if (topic.style) {
      setSelectedPrimaryStyle(topic.style.primary || '');
      setSelectedSecondaryStyle(topic.style.secondary || '');
    } else {
      setSelectedPrimaryStyle('');
      setSelectedSecondaryStyle('');
    }
  };

  const generateSuggestionsForTopic = async (topicText, description = '') => {
    try {
      setGeneratingSuggestions(true);
      const res = await fetch('/api/content/generate-suggestions', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ topic: topicText }),
      });

      if (res.ok) {
        const data = await res.json();
        const topicWithSuggestions = {
          topic: topicText,
          description: description,
          isCustom: true,
          suggested_theses: data.theses || [],
          suggested_outcomes: data.outcomes || [],
        };
        selectTopic(topicWithSuggestions);
      } else {
        // If endpoint doesn't exist or fails, proceed without suggestions
        setSelectedTopic({ topic: topicText, description: description, isCustom: true });
      }
    } catch (err) {
      // Proceed without suggestions if there's an error
      setSelectedTopic({ topic: topicText, description: description, isCustom: true });
    } finally {
      setGeneratingSuggestions(false);
    }
  };

  const generateDraft = async () => {
    if (!selectedTopic) return;

    const thesis = customThesis || selectedThesis;
    const outcome = customOutcome || selectedOutcome;

    if (!thesis || !outcome) {
      setModalError('Please select or enter a thesis and outcome before generating.');
      return;
    }

    try {
      setModalError('');
      setGeneratingDraft(true);
      // Build description from trend context if not explicitly set
      let description = selectedTopic.description || '';
      if (!description && (selectedTopic.trend_signal || selectedTopic.their_take || selectedTopic.why_it_matters)) {
        const parts = [];
        if (selectedTopic.your_angle) parts.push(`The author's unique angle on this topic: ${selectedTopic.your_angle}`);
        if (selectedTopic.their_take) parts.push(`Industry context (for background only — do NOT quote or name this person, write entirely in the author's voice): ${selectedTopic.their_take}`);
        if (selectedTopic.trend_signal) parts.push(`Trend signal: ${selectedTopic.trend_signal}`);
        if (selectedTopic.why_it_matters) parts.push(`Why it matters: ${selectedTopic.why_it_matters}`);
        if (selectedTopic.timing) parts.push(`Timing: ${selectedTopic.timing}`);
        description = parts.join('\n');
      }

      const res = await fetch('/api/content/drafts', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          topic: selectedTopic.topic,
          description,
          thesis,
          outcome,
          platform: 'blog',
          primary_style: selectedPrimaryStyle || undefined,
          secondary_style: selectedSecondaryStyle || undefined,
          structural_elements: selectedElements,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // Refresh drafts list
        await loadData();
        // Close modal and reset
        setShowGenerateModal(false);
        resetGenerateState();
        // Open preview of new draft
        setSelectedDraft(data.draft);
        setPreviewThesis(data.draft.chosen_thesis || data.draft.suggested_thesis || '');
        setPreviewOutcome(data.draft.chosen_outcome || data.draft.suggested_outcome || '');
        setPreviewPrimaryStyle(selectedPrimaryStyle);
        setPreviewSecondaryStyle(selectedSecondaryStyle);
        setPreviewElements(data.draft.structural_elements || DEFAULT_ELEMENTS);
        setFeaturedImageUrl(null);
        setShowPreviewModal(true);
      } else {
        const data = await res.json();
        setModalError(data.error || 'Failed to generate draft');
      }
    } catch (err) {
      setModalError('Failed to generate draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const resetGenerateState = () => {
    setSuggestedTopics([]);
    setSelectedTopic(null);
    setSelectedThesis('');
    setSelectedOutcome('');
    setCustomThesis('');
    setCustomOutcome('');
    setCustomTopicMode(false);
    setCustomTopicInput('');
    setCustomTopicDescription('');
    setSelectedPrimaryStyle('');
    setSelectedSecondaryStyle('');
    setSelectedElements(DEFAULT_ELEMENTS);
    setModalError('');
  };

  const approveDraft = async (draftId) => {
    try {
      const res = await fetch(`/api/content/drafts/${draftId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.stats);
        await loadData();
        setShowPreviewModal(false);
      }
    } catch (err) {
      setError('Failed to approve draft');
    }
  };

  const openEditModal = (draft) => {
    setSelectedDraft(draft);
    setEditTitle(draft.title);
    setEditBody(draft.body);
    setEditThesis(draft.chosen_thesis || draft.suggested_thesis);
    setEditOutcome(draft.chosen_outcome || draft.suggested_outcome);
    setShowEditModal(true);
  };

  const saveDraftEdits = async () => {
    if (!selectedDraft) return;

    try {
      const res = await fetch(`/api/content/drafts/${selectedDraft.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: editTitle,
          body: editBody,
          chosen_thesis: editThesis,
          chosen_outcome: editOutcome,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.stats);
        await loadData();
        setShowEditModal(false);
        setShowPreviewModal(false);
      }
    } catch (err) {
      setError('Failed to save edits');
    }
  };

  const rejectDraft = async (draftId) => {
    if (!window.confirm('Are you sure you want to reject this draft?')) return;

    try {
      const res = await fetch(`/api/content/drafts/${draftId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        await loadData();
        setShowPreviewModal(false);
      }
    } catch (err) {
      setError('Failed to reject draft');
    }
  };

  const regenerateDraft = async (draft) => {
    const topic = draft.topic || draft.title;
    // Use the editable preview values
    const thesis = previewThesis;
    const outcome = previewOutcome;

    if (!thesis || !outcome) {
      setError('Cannot regenerate: please enter both thesis and outcome');
      return;
    }

    try {
      setRegeneratingDraft(true);
      const res = await fetch('/api/content/drafts', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          topic,
          thesis,
          outcome,
          platform: 'blog',
          primary_style: previewPrimaryStyle || undefined,
          secondary_style: previewSecondaryStyle || undefined,
          structural_elements: previewElements,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // Refresh drafts list
        await loadData();
        // Show the new draft and update preview values
        setSelectedDraft(data.draft);
        setPreviewThesis(data.draft.chosen_thesis || data.draft.suggested_thesis || '');
        setPreviewOutcome(data.draft.chosen_outcome || data.draft.suggested_outcome || '');
        setPreviewElements(data.draft.structural_elements || DEFAULT_ELEMENTS);
        setFeaturedImageUrl(null);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to regenerate draft');
      }
    } catch (err) {
      setError('Failed to regenerate draft');
    } finally {
      setRegeneratingDraft(false);
    }
  };

  const downloadDraft = (draft) => {
    const content = `# ${draft.title}\n\n${draft.body}`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePublishToWordPress = async (post) => {
    // Get the category to use (from state or draft)
    const categoryToUse = selectedCategory || post.recommended_category || '';
    const categoryInfo = categoryToUse ? ` in category "${categoryToUse}"` : '';

    if (!window.confirm(`Publish this post to WordPress${categoryInfo}?`)) return;

    try {
      const res = await fetch(`/api/publish/blog-post/${post.id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          categories: categoryToUse ? [categoryToUse] : [],
          tags: []
        })
      });

      // Check if response is OK before parsing JSON
      if (!res.ok) {
        const errorText = await res.text();
        let errorMsg;
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData.error || errorData.message || `Server error (${res.status})`;
        } catch {
          errorMsg = `Server error (${res.status}): ${errorText.substring(0, 100)}`;
        }
        alert(`Error: ${errorMsg}`);
        return;
      }

      const data = await res.json();

      if (data.success) {
        const categoryMsg = data.category ? ` in category "${data.category}"` : '';
        alert(`Published${categoryMsg}!\n\nView at: ${data.url}`);
        // Refresh the list
        loadData();
        setShowPreviewModal(false);
      } else {
        alert(`Error: ${data.error || data.message || 'Unknown error occurred'}`);
      }
    } catch (err) {
      console.error('Publish error:', err);
      alert(`Error: ${err.message || 'Request failed - please check your WordPress connection'}`);
    }
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading content...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header with stats */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1>Long Form Posts</h1>
          <p className="text-muted">
            Authority-building blog posts and articles crafted in your unique voice.
          </p>
        </div>
        <div className="text-end">
          {profile && profile.correction_streak > 0 && (
            <div
              style={{
                background: `linear-gradient(135deg, ${colors.goldLight}, ${colors.goldMid})`,
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                color: colors.greyDarkest,
              }}
            >
              <div style={{ fontSize: '0.8rem' }}>Correction Streak</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {profile.correction_streak} posts
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Readiness Warning */}
      {readiness && !readiness.is_ready && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading className="h6 mb-2">
            Complete Your Profile for Better Content
          </Alert.Heading>
          <p className="mb-2 small">
            We can generate content now, but it will be much better once you complete these:
          </p>
          <ul className="mb-2 small">
            {readiness.missing.map((item, idx) => (
              <li key={idx}>
                <strong>{item.item}</strong>: {item.description}{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0"
                  onClick={() => navigate(item.link)}
                >
                  Complete now
                </Button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Recommended Enhancements */}
      {readiness && readiness.is_ready && readiness.warnings.length > 0 && (
        <Alert className="mb-4" dismissible style={{ backgroundColor: 'rgba(204,165,87,0.15)', borderColor: colors.goldMid, color: colors.greyDarkest }}>
          <strong>Pro tip:</strong> Enhance your content quality by completing:{' '}
          {readiness.warnings.map((item, idx) => (
            <span key={idx}>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={() => navigate(item.link)}
                style={{ color: colors.goldDark }}
              >
                {item.item}
              </Button>
              {idx < readiness.warnings.length - 1 ? ', ' : ''}
            </span>
          ))}
        </Alert>
      )}

      {/* Business Context - affects topic variety */}
      <Card className="mb-4" style={{ borderColor: colors.goldLight }}>
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ background: colors.goldLight, color: colors.greyDarkest }}
        >
          <span>
            <strong>Topic Generation Context</strong>
            <span className="text-muted ms-2 small">
              (expand these for more varied topics)
            </span>
          </span>
          {!editingBusinessContext ? (
            <Button
              variant="link"
              size="sm"
              className="p-0"
              style={{ color: colors.greyDarkest }}
              onClick={() => setEditingBusinessContext(true)}
            >
              Edit
            </Button>
          ) : (
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setEditingBusinessContext(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={saveBusinessContext}
                disabled={savingBusinessContext}
              >
                {savingBusinessContext ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </Card.Header>
        <Card.Body className="py-2">
          {!editingBusinessContext ? (
            <Row>
              <Col md={6}>
                <div className="small text-muted">Core Product/Service:</div>
                <div className="small">
                  {businessContext.core_product || <em className="text-muted">Not set</em>}
                </div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Features & Benefits:</div>
                <div className="small">
                  {businessContext.features_benefits || <em className="text-muted">Not set</em>}
                </div>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Core Product/Service</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={businessContext.core_product}
                    onChange={(e) =>
                      setBusinessContext({ ...businessContext, core_product: e.target.value })
                    }
                    placeholder="Describe what you sell..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Features & Benefits</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={businessContext.features_benefits}
                    onChange={(e) =>
                      setBusinessContext({ ...businessContext, features_benefits: e.target.value })
                    }
                    placeholder="Key features and benefits..."
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Topic Mode Selector */}
      <div className="mb-3">
        <div className="small text-muted mb-2" style={{ fontWeight: 500 }}>Topic Discovery Mode</div>
        <div className="d-flex flex-column gap-1">
          {[
            { key: 'popular', label: 'Popular', desc: 'Buzzy, high-engagement topics' },
            { key: 'emerging', label: 'Emerging Trends', desc: 'Early signals before they go mainstream' },
            { key: 'latest', label: 'Latest News', desc: 'Recent developments from the past 1-2 weeks' },
          ].map((mode) => (
            <Form.Check
              key={mode.key}
              type="radio"
              id={`topic-mode-${mode.key}`}
              name="topicMode"
              checked={topicMode === mode.key}
              onChange={() => setTopicMode(mode.key)}
              label={<span>{mode.label} <span className="text-muted small">— {mode.desc}</span></span>}
            />
          ))}
          {hasThoughtLeaders ? (
            <Form.Check
              type="radio"
              id="topic-mode-thought-leaders"
              name="topicMode"
              checked={topicMode === 'thought_leader'}
              onChange={() => setTopicMode('thought_leader')}
              label={<span>Thought Leaders <span className="text-muted small">— Topics inspired by your industry thought leaders</span></span>}
            />
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Add thought leaders in Market Intelligence to unlock this</Tooltip>}
            >
              <div>
                <Form.Check
                  type="radio"
                  id="topic-mode-thought-leaders"
                  name="topicMode"
                  disabled
                  label={<span style={{ color: colors.greyLight }}>Thought Leaders — <a href="/command-center/market-intelligence" style={{ color: colors.goldMid, fontSize: '0.85rem' }}>Add thought leaders to unlock</a></span>}
                />
              </div>
            </OverlayTrigger>
          )}
        </div>
      </div>

      {/* Generate New Buttons */}
      <div className="mb-4 d-flex gap-2">
        <Button
          onClick={() => {
            setShowGenerateModal(true);
            generateTopics();
          }}
          style={{
            background: colors.goldMid,
            border: 'none',
            color: colors.greyDarkest,
          }}
        >
          + Generate New Topics
        </Button>
        <Button
          variant="outline-dark"
          onClick={() => {
            setCustomTopicMode(true);
            setShowGenerateModal(true);
          }}
        >
          + Enter a Topic to Draft
        </Button>
      </div>

      {/* Tabs for Pending and Approved */}
      <Tabs defaultActiveKey="pending" className="mb-4">
        <Tab eventKey="pending" title={`Pending Approval (${drafts.length})`}>
          {drafts.length === 0 ? (
            <Card className="p-4 text-center text-muted">
              <p className="mb-0">No drafts pending approval.</p>
              <p className="small">Generate a new post to get started.</p>
            </Card>
          ) : (
            <Row>
              {drafts.map((draft) => (
                <Col key={draft.id} md={6} lg={4} className="mb-3">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <Card.Title
                        style={{
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {draft.title}
                      </Card.Title>
                      <Card.Text className="text-muted small">
                        Created {new Date(draft.created_at).toLocaleDateString()}
                      </Card.Text>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setSelectedDraft(draft);
                            setPreviewThesis(draft.chosen_thesis || draft.suggested_thesis || '');
                            setPreviewOutcome(draft.chosen_outcome || draft.suggested_outcome || '');
                            setPreviewPrimaryStyle(draft.primary_style || '');
                            setPreviewSecondaryStyle(draft.secondary_style || '');
                            setPreviewElements(draft.structural_elements || DEFAULT_ELEMENTS);
                            setFeaturedImageUrl(draft.featured_image_url || null);
                            setSelectedCategory(draft.recommended_category || '');
                            if (draft.recommended_category) fetchCategories();
                            setShowPreviewModal(true);
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => approveDraft(draft.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEditModal(draft)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => rejectDraft(draft.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="approved" title={`Approved (${approvedPosts.length})`}>
          {approvedPosts.length === 0 ? (
            <Card className="p-4 text-center text-muted">
              <p className="mb-0">No approved posts yet.</p>
            </Card>
          ) : (
            <Row>
              {approvedPosts.map((post) => (
                <Col key={post.id} md={6} lg={4} className="mb-3">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <Card.Title
                          style={{
                            fontSize: '1rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {post.title}
                        </Card.Title>
                        {post.was_corrected && (
                          <Badge bg="info" className="ms-2">
                            Edited
                          </Badge>
                        )}
                      </div>
                      <Card.Text className="text-muted small">
                        Approved {new Date(post.approved_at).toLocaleDateString()}
                      </Card.Text>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setSelectedDraft(post);
                            setPreviewThesis(post.chosen_thesis || post.suggested_thesis || '');
                            setPreviewOutcome(post.chosen_outcome || post.suggested_outcome || '');
                            setPreviewPrimaryStyle(post.primary_style || '');
                            setPreviewSecondaryStyle(post.secondary_style || '');
                            setPreviewElements(post.structural_elements || DEFAULT_ELEMENTS);
                            setFeaturedImageUrl(post.featured_image_url || null);
                            setSelectedCategory(post.recommended_category || '');
                            if (post.recommended_category) fetchCategories();
                            setShowPreviewModal(true);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => downloadDraft(post)}
                        >
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handlePublishToWordPress(post)}
                        >
                          Publish
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>

      {/* Generate Modal */}
      <Modal
        show={showGenerateModal}
        onHide={() => {
          setShowGenerateModal(false);
          resetGenerateState();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{customTopicMode ? 'Draft Your Topic' : 'Generate New Post'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <Alert variant="danger" onClose={() => setModalError('')} dismissible>
              {modalError}
            </Alert>
          )}
          {/* Custom Topic Mode - Enter your own topic */}
          {customTopicMode && !selectedTopic ? (
            <div>
              <h5>Step 1: Enter Your Topic</h5>
              <Form.Group className="mb-3">
                <Form.Label>What do you want to write about?</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Why most entrepreneurs fail at delegation"
                  value={customTopicInput}
                  onChange={(e) => setCustomTopicInput(e.target.value)}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  Enter a topic, title, or idea you want to explore
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>What should this article cover? <span className="text-muted">(optional)</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="e.g., I want to talk about how AI can handle the repetitive tasks so entrepreneurs can focus on strategy. Include a story about a client who was drowning in busywork before using automation."
                  value={customTopicDescription}
                  onChange={(e) => setCustomTopicDescription(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Add any specific points, stories, or angles you want included
                </Form.Text>
              </Form.Group>
              <Button
                onClick={() => {
                  if (customTopicInput.trim()) {
                    generateSuggestionsForTopic(customTopicInput.trim(), customTopicDescription.trim());
                  }
                }}
                disabled={!customTopicInput.trim() || generatingSuggestions}
                style={{
                  background: colors.goldMid,
                  border: 'none',
                  color: colors.greyDarkest,
                }}
              >
                {generatingSuggestions ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating suggestions...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          ) : !selectedTopic ? (
            /* AI-generated topic suggestions */
            <div>
              <h5>Step 1: Select a Topic</h5>
              {generatingTopics ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Generating topic suggestions...</p>
                </div>
              ) : suggestedTopics.length > 0 ? (
                <div className="mt-3">
                  {suggestedTopics.map((topic, idx) => {
                    const overallScore = topic.score?.overall;
                    const isTopPick = overallScore >= 4.5;
                    const isWeak = overallScore != null && overallScore < 4.0;
                    return (
                    <Card
                      key={idx}
                      className="mb-2"
                      style={{
                        cursor: 'pointer',
                        opacity: isWeak ? 0.6 : 1,
                        borderLeft: isTopPick
                          ? `4px solid ${colors.goldMid}`
                          : isWeak
                            ? `4px solid ${colors.greyLight}`
                            : undefined,
                      }}
                      onClick={() => selectTopic(topic)}
                    >
                      <Card.Body className="py-2">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ fontWeight: '600', flex: 1 }}>{topic.topic}</div>
                          {topic.score && (
                            <div style={{ marginLeft: 12, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <StarRating score={overallScore} size={14} />
                              <span className="ms-1 small" style={{ color: colors.greyMid }}>{overallScore.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-muted small">
                          {topic.their_take || topic.trend_signal || topic.buzz_signal || topic.development || topic.angle}
                        </div>
                        {topic.thought_leader && (
                          <div className="small mt-1" style={{ color: colors.goldDark }}>
                            <strong>Inspired by:</strong> {topic.thought_leader}
                          </div>
                        )}
                        {topic.your_angle && (
                          <div className="text-muted small mt-1">
                            <em>Your angle:</em> {topic.your_angle}
                          </div>
                        )}
                        {topic.why_it_matters && (
                          <div className="text-muted small mt-1">
                            <em>Why now:</em> {topic.why_it_matters}
                          </div>
                        )}
                        {topic.score?.reason && (
                          <div className="small mt-1" style={{ color: colors.goldDark, fontStyle: 'italic' }}>
                            {topic.score.reason}
                          </div>
                        )}
                        {topic.style && (
                          <div className="mt-2">
                            <Badge bg="primary" className="me-1" style={{ fontSize: '0.7rem' }}>
                              {PRIMARY_STYLES[topic.style.primary]?.name || topic.style.primary_name}
                            </Badge>
                            {topic.style.secondary && (
                              <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                {SECONDARY_STYLES[topic.style.secondary]?.name || topic.style.secondary_name}
                              </Badge>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    );
                  })}
                  <Button
                    variant="link"
                    className="mt-2"
                    onClick={generateTopics}
                  >
                    Regenerate suggestions
                  </Button>
                </div>
              ) : (
                <Button onClick={generateTopics}>Generate Topic Suggestions</Button>
              )}
            </div>
          ) : (
            /* Step 2: Select Thesis and Outcome */
            <div>
              <h5>Step 2: Choose Your Angle</h5>
              <Card className="mb-3 bg-light">
                <Card.Body className="py-2">
                  <div className="small text-muted">Topic:</div>
                  <div style={{ fontWeight: '600' }}>{selectedTopic.topic}</div>
                </Card.Body>
              </Card>

              <Form.Group className="mb-3">
                <Form.Label>Strategic Thesis <span className="text-danger">*</span></Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  The core argument or perspective of your piece
                </Form.Text>
                {selectedTopic.suggested_theses?.map((thesis, idx) => (
                  <Form.Check
                    key={idx}
                    type="radio"
                    id={`thesis-${idx}`}
                    name="thesis"
                    label={thesis}
                    checked={selectedThesis === thesis && !customThesis}
                    onChange={() => {
                      setSelectedThesis(thesis);
                      setCustomThesis('');
                    }}
                    className="mb-2"
                  />
                ))}
                <Form.Control
                  type="text"
                  placeholder={selectedTopic.isCustom ? "What's the main point you want to make?" : "Or enter your own thesis..."}
                  value={customThesis}
                  onChange={(e) => {
                    setCustomThesis(e.target.value);
                    if (e.target.value) setSelectedThesis('');
                  }}
                  className={selectedTopic.suggested_theses?.length ? "mt-2" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Desired Outcome <span className="text-danger">*</span></Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  What cognitive shift should happen in the reader?
                </Form.Text>
                {selectedTopic.suggested_outcomes?.map((outcome, idx) => (
                  <Form.Check
                    key={idx}
                    type="radio"
                    id={`outcome-${idx}`}
                    name="outcome"
                    label={outcome}
                    checked={selectedOutcome === outcome && !customOutcome}
                    onChange={() => {
                      setSelectedOutcome(outcome);
                      setCustomOutcome('');
                    }}
                    className="mb-2"
                  />
                ))}
                <Form.Control
                  type="text"
                  placeholder={selectedTopic.isCustom ? "What should readers think or do differently?" : "Or enter your own outcome..."}
                  value={customOutcome}
                  onChange={(e) => {
                    setCustomOutcome(e.target.value);
                    if (e.target.value) setSelectedOutcome('');
                  }}
                  className={selectedTopic.suggested_outcomes?.length ? "mt-2" : ""}
                />
              </Form.Group>

              {/* Writing Style Selection */}
              <Form.Group className="mb-3">
                <Form.Label>Writing Style</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  How should this piece be framed? (auto-selected based on your archetype)
                </Form.Text>
                <Row>
                  <Col md={6}>
                    <Form.Select
                      value={selectedPrimaryStyle}
                      onChange={(e) => setSelectedPrimaryStyle(e.target.value)}
                      className="mb-2"
                    >
                      <option value="">Auto-select primary style</option>
                      {Object.entries(PRIMARY_STYLES).map(([key, style]) => (
                        <option key={key} value={key}>{style.name}</option>
                      ))}
                    </Form.Select>
                    {selectedPrimaryStyle && (
                      <div className="text-muted small">
                        {PRIMARY_STYLES[selectedPrimaryStyle]?.description}
                      </div>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      value={selectedSecondaryStyle}
                      onChange={(e) => setSelectedSecondaryStyle(e.target.value)}
                      className="mb-2"
                    >
                      <option value="">No secondary style</option>
                      {Object.entries(SECONDARY_STYLES).map(([key, style]) => (
                        <option key={key} value={key}>{style.name}</option>
                      ))}
                    </Form.Select>
                    {selectedSecondaryStyle && (
                      <div className="text-muted small">
                        {SECONDARY_STYLES[selectedSecondaryStyle]?.description}
                      </div>
                    )}
                  </Col>
                </Row>
              </Form.Group>

              {/* Content Elements */}
              <Form.Group className="mb-3">
                <Form.Label>Content Elements</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  Structural elements to include in the draft
                </Form.Text>
                <div className="d-flex gap-2 flex-wrap">
                  {Object.entries(STRUCTURAL_ELEMENTS).map(([key, el]) => (
                    <OverlayTrigger
                      key={key}
                      placement="top"
                      overlay={<Tooltip>{el.description}</Tooltip>}
                    >
                      <Badge
                        bg={selectedElements.includes(key) ? 'dark' : ''}
                        text={selectedElements.includes(key) ? '' : 'dark'}
                        style={{
                          cursor: 'pointer',
                          border: selectedElements.includes(key) ? 'none' : `1px solid ${colors.greyLight}`,
                          backgroundColor: selectedElements.includes(key) ? colors.greyDarkest : 'transparent',
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                        }}
                        onClick={() => {
                          setSelectedElements(prev =>
                            prev.includes(key)
                              ? prev.filter(e => e !== key)
                              : [...prev, key]
                          );
                        }}
                      >
                        {el.name}
                      </Badge>
                    </OverlayTrigger>
                  ))}
                </div>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setSelectedTopic(null)}
                >
                  Back
                </Button>
                <Button
                  onClick={generateDraft}
                  disabled={generatingDraft}
                  style={{
                    background: colors.goldMid,
                    border: 'none',
                    color: colors.greyDarkest,
                  }}
                >
                  {generatingDraft ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    'Generate Draft'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Preview Modal */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedDraft?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedDraft && (
            <div>
              {selectedDraft.status === 'draft' ? (
                <div className="mb-3 p-3 bg-light rounded">
                  <Form.Group className="mb-2">
                    <Form.Label className="small text-muted mb-1">Strategic Thesis:</Form.Label>
                    <Form.Control
                      type="text"
                      value={previewThesis}
                      onChange={(e) => setPreviewThesis(e.target.value)}
                      placeholder="The core argument of your piece"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">Desired Outcome:</Form.Label>
                    <Form.Control
                      type="text"
                      value={previewOutcome}
                      onChange={(e) => setPreviewOutcome(e.target.value)}
                      placeholder="What shift should happen in the reader?"
                    />
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label className="small text-muted mb-1">Writing Style:</Form.Label>
                    <Row>
                      <Col md={6}>
                        <Form.Select
                          size="sm"
                          value={previewPrimaryStyle}
                          onChange={(e) => setPreviewPrimaryStyle(e.target.value)}
                        >
                          <option value="">Auto-select primary</option>
                          {Object.entries(PRIMARY_STYLES).map(([key, style]) => (
                            <option key={key} value={key}>{style.name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Select
                          size="sm"
                          value={previewSecondaryStyle}
                          onChange={(e) => setPreviewSecondaryStyle(e.target.value)}
                        >
                          <option value="">No secondary style</option>
                          {Object.entries(SECONDARY_STYLES).map(([key, style]) => (
                            <option key={key} value={key}>{style.name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                    </Row>
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label className="small text-muted mb-1">Content Elements:</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {Object.entries(STRUCTURAL_ELEMENTS).map(([key, el]) => (
                        <OverlayTrigger
                          key={key}
                          placement="top"
                          overlay={<Tooltip>{el.description}</Tooltip>}
                        >
                          <Badge
                            bg={previewElements.includes(key) ? 'dark' : ''}
                            text={previewElements.includes(key) ? '' : 'dark'}
                            style={{
                              cursor: 'pointer',
                              border: previewElements.includes(key) ? 'none' : `1px solid ${colors.greyLight}`,
                              backgroundColor: previewElements.includes(key) ? colors.greyDarkest : 'transparent',
                              padding: '6px 10px',
                              fontSize: '0.8rem',
                            }}
                            onClick={() => {
                              setPreviewElements(prev =>
                                prev.includes(key)
                                  ? prev.filter(e => e !== key)
                                  : [...prev, key]
                              );
                            }}
                          >
                            {el.name}
                          </Badge>
                        </OverlayTrigger>
                      ))}
                    </div>
                  </Form.Group>
                  <div className="small text-muted mt-2">
                    Edit these and click Regenerate to try a different angle
                  </div>
                </div>
              ) : (
                <div className="mb-3 p-2 bg-light rounded">
                  <div className="small text-muted">Strategic Thesis:</div>
                  <div>{selectedDraft.chosen_thesis || selectedDraft.suggested_thesis}</div>
                  <div className="small text-muted mt-2">Desired Outcome:</div>
                  <div>{selectedDraft.chosen_outcome || selectedDraft.suggested_outcome}</div>
                </div>
              )}
              {/* Featured Image Section */}
              <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#fafafa' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Featured Image</strong>
                    <div className="text-muted small">
                      {featuredImageUrl
                        ? 'Image generated successfully'
                        : 'Generate an AI image for this post'}
                    </div>
                  </div>
                  {featuredImageUrl ? (
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={async () => {
                          if (!selectedDraft) return;
                          setGeneratingImage(true);
                          try {
                            const res = await fetch(`/api/content/drafts/${selectedDraft.id}/generate-image`, {
                              method: 'POST',
                              headers: getAuthHeaders(),
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setFeaturedImageUrl(data.image_url);
                              if (data.recommended_category) {
                                setSelectedCategory(data.recommended_category);
                              }
                              setSelectedDraft({
                                ...selectedDraft,
                                featured_image_url: data.image_url,
                                recommended_category: data.recommended_category,
                              });
                              if (data.is_new_category) {
                                fetchCategories();
                              }
                            } else {
                              const data = await res.json();
                              setError(data.error || 'Failed to regenerate image');
                            }
                          } catch (err) {
                            setError('Failed to regenerate featured image');
                          } finally {
                            setGeneratingImage(false);
                          }
                        }}
                        disabled={generatingImage}
                      >
                        {generatingImage ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Regenerating...
                          </>
                        ) : (
                          'Regenerate Image'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setFeaturedImageUrl(null)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={async () => {
                        if (!selectedDraft) return;
                        setGeneratingImage(true);
                        // Fetch categories if not already loaded
                        if (categories.length === 0) {
                          fetchCategories();
                        }
                        try {
                          const res = await fetch(`/api/content/drafts/${selectedDraft.id}/generate-image`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setFeaturedImageUrl(data.image_url);
                            // Set AI-recommended category
                            if (data.recommended_category) {
                              setSelectedCategory(data.recommended_category);
                            }
                            // Update the draft in state
                            setSelectedDraft({
                              ...selectedDraft,
                              featured_image_url: data.image_url,
                              recommended_category: data.recommended_category,
                            });
                            // Refresh categories if a new one was suggested
                            if (data.is_new_category) {
                              fetchCategories();
                            }
                          } else {
                            const data = await res.json();
                            setError(data.error || 'Failed to generate image');
                          }
                        } catch (err) {
                          setError('Failed to generate featured image');
                        } finally {
                          setGeneratingImage(false);
                        }
                      }}
                      disabled={generatingImage}
                    >
                      {generatingImage ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Generating...
                        </>
                      ) : (
                        '🖼️ Generate Featured Image'
                      )}
                    </Button>
                  )}
                </div>
                {featuredImageUrl && (
                  <div className="mt-3">
                    <img
                      src={featuredImageUrl}
                      alt="Featured"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Category Selection - shows after image generation */}
              {(selectedCategory || selectedDraft?.recommended_category) && (
                <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#fafafa' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Blog Category</strong>
                      <div className="text-muted small">
                        AI-recommended based on your post content
                      </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Form.Select
                      value={selectedCategory || selectedDraft?.recommended_category || ''}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={loadingCategories}
                    >
                      {loadingCategories ? (
                        <option>Loading categories...</option>
                      ) : (
                        <>
                          {/* Show recommended category first if it's a new suggestion */}
                          {selectedDraft?.recommended_category &&
                           !categories.find(c => c.name === selectedDraft.recommended_category) && (
                            <option value={selectedDraft.recommended_category}>
                              {selectedDraft.recommended_category} (new - will be created)
                            </option>
                          )}
                          {categories.map((cat) => (
                            <option key={cat.wp_category_id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                        </>
                      )}
                    </Form.Select>
                  </div>
                </div>
              )}

              <div
                className="blog-content-preview"
                style={{
                  fontFamily: 'Georgia, serif',
                  lineHeight: '1.8',
                }}
                dangerouslySetInnerHTML={{ __html: selectedDraft.body }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedDraft?.status === 'draft' && (
            <>
              <Button variant="outline-danger" onClick={() => rejectDraft(selectedDraft.id)}>
                Reject
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => regenerateDraft(selectedDraft)}
                disabled={regeneratingDraft}
              >
                {regeneratingDraft ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Regenerating...
                  </>
                ) : (
                  '🔄 Regenerate'
                )}
              </Button>
              <Button variant="outline-secondary" onClick={() => openEditModal(selectedDraft)}>
                Edit
              </Button>
              <Button
                variant="success"
                onClick={() => approveDraft(selectedDraft.id)}
              >
                Approve
              </Button>
            </>
          )}
          {selectedDraft?.status === 'approved' && (
            <>
              <Button variant="outline-secondary" onClick={() => downloadDraft(selectedDraft)}>
                Download Markdown
              </Button>
              <Button
                variant="secondary"
                onClick={() => handlePublishToWordPress(selectedDraft)}
              >
                Publish to WordPress
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Draft</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Strategic Thesis</Form.Label>
            <Form.Control
              type="text"
              value={editThesis}
              onChange={(e) => setEditThesis(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Desired Outcome</Form.Label>
            <Form.Control
              type="text"
              value={editOutcome}
              onChange={(e) => setEditOutcome(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Content</Form.Label>
            <RichTextEditor
              key={selectedDraft?.id || 'edit'}
              value={editBody}
              onChange={(content) => setEditBody(content)}
              height="400px"
              toolbar="full"
              mode="html"
            />
          </Form.Group>

          <Alert variant="info">
            Editing and saving will reset your correction streak and mark this as a corrected post.
            This helps the AI learn your preferences.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={saveDraftEdits}
            style={{
              background: colors.goldMid,
              border: 'none',
              color: colors.greyDarkest,
            }}
          >
            Save & Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default LongFormPosts;
